/**
 * V213.1: Android Back Button Hook with Priority System
 * 
 * Provides a unified back button handling system with priority-based handlers.
 * Higher priority handlers are executed first. If a handler returns true,
 * it means the event was handled and lower priority handlers won't be called.
 * 
 * Priority guidelines:
 * - Modals/Dialogs: 100
 * - Bottom sheets: 90
 * - Side panels: 80
 * - Other overlays: 70
 * - Default navigation: 0
 * 
 * Usage:
 * ```tsx
 * import { useAndroidBackButton } from '@/hooks/useAndroidBackButton';
 * 
 * function MyDialog({ open, onClose }) {
 *   useAndroidBackButton(
 *     'my-dialog',
 *     () => {
 *       if (open) {
 *         onClose();
 *         return true; // Handled
 *       }
 *       return false; // Not handled
 *     },
 *     100, // High priority for modals
 *     [open, onClose]
 *   );
 * }
 * ```
 */

import { useEffect, type DependencyList } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

interface BackButtonHandler {
  id: string;
  priority: number;
  handler: () => boolean;
}

class BackButtonManager {
  private handlers: Map<string, BackButtonHandler> = new Map();
  private initialized = false;
  private navigateCallback: ((delta: number) => void) | null = null;

  register(id: string, priority: number, handler: () => boolean) {
    this.handlers.set(id, { id, priority, handler });
    console.log('[V213 Back] Registered:', id, 'priority:', priority);
  }

  unregister(id: string) {
    this.handlers.delete(id);
    console.log('[V213 Back] Unregistered:', id);
  }

  setNavigateCallback(callback: (delta: number) => void) {
    this.navigateCallback = callback;
  }

  async handleBackButton(canGoBack: boolean): Promise<void> {
    console.log('[V213 Back] ========================================');
    console.log('[V213 Back] Button pressed');
    console.log('[V213 Back] Can go back:', canGoBack);
    console.log('[V213 Back] Registered handlers:', this.handlers.size);

    // Sort handlers by priority (highest first)
    const sorted = Array.from(this.handlers.values())
      .sort((a, b) => b.priority - a.priority);

    console.log('[V213 Back] Trying handlers:', sorted.map(h => `${h.id}(${h.priority})`).join(', '));

    // Try each handler
    for (const { id, handler } of sorted) {
      try {
        const handled = handler();
        if (handled) {
          console.log('[V213 Back] ✅ Handled by:', id);
          console.log('[V213 Back] ========================================');
          return;
        }
      } catch (error) {
        console.error('[V213 Back] Handler error:', id, error);
      }
    }

    // No custom handler handled it
    // If can navigate back, do it
    if (canGoBack && this.navigateCallback) {
      console.log('[V213 Back] Navigating back');
      this.navigateCallback(-1);
    } else {
      // On root route, minimize app (don't exit)
      console.log('[V213 Back] Minimizing app (root route)');
      try {
        await CapApp.minimizeApp();
      } catch (error) {
        console.error('[V213 Back] Failed to minimize:', error);
      }
    }
    console.log('[V213 Back] ========================================');
  }

  initialize() {
    if (this.initialized || !Capacitor.isNativePlatform()) {
      return;
    }

    CapApp.addListener('backButton', ({ canGoBack }) => {
      this.handleBackButton(canGoBack);
    });

    this.initialized = true;
    console.log('[V213 Back] ✅ Listener initialized');
  }
}

// Singleton instance
const backButtonManager = new BackButtonManager();

/**
 * Hook to register a back button handler with priority.
 * 
 * @param id - Unique identifier for this handler
 * @param handler - Function that returns true if back was handled
 * @param priority - Higher priority handlers are called first (default: 0)
 * @param deps - Dependencies array for the handler
 */
export function useAndroidBackButton(
  id: string,
  handler: () => boolean,
  priority: number = 0,
  deps: DependencyList = []
) {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    backButtonManager.register(id, priority, handler);

    return () => {
      backButtonManager.unregister(id);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, priority, ...deps]);
}

/**
 * Initialize the back button listener. Call once in App.tsx.
 * 
 * @param navigate - Function to navigate (e.g., from useNavigate hook)
 */
export function initializeAndroidBackButton(navigate: (delta: number) => void) {
  backButtonManager.setNavigateCallback(navigate);
  backButtonManager.initialize();
}

export { backButtonManager };

